import { ArgEntry } from '../../resources/types';
import {
  Attach,
  NoParams,
  Resolver,
  ResolverHandle,
  ResolverRuntime,
} from './resolver';

export interface DecorateBuilder<TParams, TData, TError extends Error = Error>
  extends Resolver<TParams, TData, TError> {
  with: <TNewParams>(
    decorator: (
      resolver: Resolver<TParams, TData, TError>
    ) => Resolver<TNewParams, TData, TError>
  ) => DecorateBuilder<TNewParams, TData, TError>;
  when: (
    predicate: (params: TParams) => boolean,
    decorator: (
      resolver: Resolver<TParams, TData, TError>
    ) => Resolver<TParams, TData, TError>
  ) => DecorateBuilder<TParams, TData, TError>;
}

/**
 * A builder interface that allows for easy decorator chaining
 * with proper type inference for decorator param extension.
 *
 * @example
 * ```ts
 * const resolver = decorate(baseResolver)
 *   .with(polling({ intervalMs: 1000 }))
 *   .with(lifecycleRules);
 * ```
 */
export const decorate = <TParams, TData, TError extends Error = Error>(
  resolver: Resolver<TParams, TData, TError>
): DecorateBuilder<TParams, TData, TError> =>
  Object.freeze({
    ...resolver,
    with: <TNewParams>(
      decorator: (
        resolver: Resolver<TParams, TData, TError>
      ) => Resolver<TNewParams, TData, TError>
    ) => decorate(decorator(resolver)),
    when: (
      predicate: (params: TParams) => boolean,
      decorator: (
        resolver: Resolver<TParams, TData, TError>
      ) => Resolver<TParams, TData, TError>
    ) => {
      const decorated = decorator(resolver);
      const addedTags = decorated.tags.filter(
        (t) => !resolver.tags.includes(t)
      );
      const argField = (arg: ArgEntry<TParams>) =>
        typeof arg === 'string' ? arg : arg.field;
      const baseFields = new Set((resolver.args ?? []).map(argField));
      return decorate<TParams, TData, TError>({
        name: resolver.name,
        tags: [...resolver.tags, ...addedTags.map((t) => `when(${t})`)],
        args: [
          ...(resolver.args ?? []),
          ...(decorated.args ?? []).filter(
            (arg) => !baseFields.has(argField(arg))
          ),
        ],
        attach: (params, runtime) => {
          if (predicate(params)) {
            return decorated.attach(params, runtime);
          }
          return resolver.attach(params, runtime);
        },
      });
    },
  }) as DecorateBuilder<TParams, TData, TError>;

// A helper type to strip NoParams if a decorator is extending a no-param resolver
type ExtendParams<TCurrentParams, TAddedParams> =
  TCurrentParams extends NoParams
    ? TAddedParams
    : TCurrentParams & TAddedParams;

export type PassthroughDecorator<TParams, TData> = (
  resolver: Resolver<TParams, TData>
) => Resolver<TParams, TData>;

// A decorator that doesn't extend params
export function createDecorator(
  name: string,
  wrapAttach: <TParams, TData>(
    originalAttach: Attach<TParams, TData>
  ) => (
    baseParams: TParams,
    runtime: ResolverRuntime<TParams, TData>
  ) => ResolverHandle
): <TParams, TData, TError extends Error = Error>(
  resolver: Resolver<TParams, TData, TError>
) => Resolver<TParams, TData, TError>;

// A decorator that extends params
export function createDecorator<TAddedParams extends Record<string, unknown>>(
  name: string,
  wrapAttach: <TParams, TData>(
    originalAttach: Attach<TParams, TData>
  ) => (
    baseParams: TParams,
    runtime: ResolverRuntime<TParams, TData>,
    addedParams: TAddedParams
  ) => ResolverHandle
): <TParams, TData, TError extends Error = Error>(
  resolver: Resolver<TParams, TData, TError>
) => Resolver<ExtendParams<TParams, TAddedParams>, TData, TError>;

/**
 * Create a decorator that wraps a resolver with additional behavior and/or params.
 *
 * Handles a few invariants:
 * - Preserves underlying resolver's name
 * - Adds decorator's name to list of tags
 * - Passes base + added (if defined) params separately to make TS happy
 *
 * @example
 * ```ts
 * // Simple case
 * const polling = (pollIntervalMs: number) => createDecorator(
 *   'polling',
 *   (attach) => (baseParams, runtime) => {
 *     const baseHandle = attach(baseParams, runtime);
 *
 *     // polling logic, return handle
 *   }
 * );
 *
 * // Param extension
 * const envAwareHttpClient = createDecorator<{ env: 'qa' | 'prod' }>(
 *  'withEnv',
 *  (attach) =>
 *    (baseParams, runtime, addedParams) =>
 *      attach(baseParams, {
 *        ...runtime,
 *         env: {
 *          ...runtime.env,
 *          httpClient: addedParams.env === 'qa'
 *            ? ({} as ResolverHttpClient)
 *            : runtime.env.httpClient,
 *         },
 *      })
 * );
 * ```
 */
export function createDecorator(name: string, wrapAttach: any) {
  return (resolver: Resolver<any, any, any>): Resolver<any, any, any> => {
    const wrappedAttach = wrapAttach(resolver.attach);

    return {
      name: resolver.name,
      tags: [...resolver.tags, name],
      args: resolver.args,
      attach: (fullParams, runtime) =>
        wrappedAttach(fullParams, runtime, fullParams),
    };
  };
}
